import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CreditCard, Banknote, XOctagon, Wallet, HandCoins, Trash2 } from "lucide-react";
import { api, money, dateShort } from "../lib/api";

const emptyPay = {
  amount: "",
  method: "efectivo",
  pan: "",
  expiry: "",
  cvv: "",
  zip_code: "",
  note: "",
};

export default function LayawayDetail() {
  const nav = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(emptyPay);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api(`/admin/layaways/${id}`)
      .then(setData)
      .catch((e) => toast.error(e.message));

  useEffect(() => {
    load();
     
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        layaway_id: id,
        amount: Number(form.amount),
        method: form.method,
        note: form.note,
      };
      if (form.method === "valor_card") {
        body.pan = form.pan;
        body.expiry = form.expiry;
        body.cvv = form.cvv;
        body.zip_code = form.zip_code;
      }
      const res = await api("/admin/payments", { method: "POST", body });
      if (res.status === "aprobado") {
        toast.success(`Pago aprobado: ${money(res.amount)}`);
      } else {
        toast.error(`Pago rechazado: ${res.valor_response_msg || "sin detalle"}`);
      }
      setForm(emptyPay);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("¿Cancelar este apartado?")) return;
    try {
      await api(`/admin/layaways/${id}/cancel`, { method: "POST" });
      toast.success("Apartado cancelado");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const forgive = async () => {
    const { layaway } = data || {};
    if (!layaway) return;
    const rem = (layaway.total_amount - layaway.paid_amount).toFixed(2);
    if (!window.confirm(`¿Perdonar el saldo restante de $${rem}? El apartado quedará marcado como "perdonado".`))
      return;
    try {
      const r = await api(`/admin/layaways/${id}/forgive`, { method: "POST" });
      toast.success(`Saldo perdonado: ${money(r.amount_forgiven)}`);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const hardDelete = async () => {
    const { layaway } = data || {};
    if (!layaway) return;
    const paid = Number(layaway.paid_amount || 0);
    let refund = false;
    if (paid > 0) {
      const ans = window.confirm(
        `Eliminar apartado ${layaway.invoice_number}.\n\n` +
          `El cliente ya pagó ${money(paid)}.\n\n` +
          `Aceptar = devolver ${money(paid)} como crédito a favor del cliente.\n` +
          `Cancelar = ver otra opción.`
      );
      if (ans) {
        refund = true;
      } else {
        const sure = window.confirm(
          `¿Eliminar SIN devolver nada? (el dinero pagado quedará en cero, sin crédito)`
        );
        if (!sure) return;
      }
    } else {
      if (!window.confirm(`Eliminar el apartado ${layaway.invoice_number}?`)) return;
    }
    try {
      const r = await api(
        `/admin/layaways/${id}?refund_as_credit=${refund ? "true" : "false"}`,
        { method: "DELETE" }
      );
      if (r.refunded_to_credit > 0) {
        toast.success(`Apartado borrado · ${money(r.refunded_to_credit)} devueltos como crédito`);
      } else {
        toast.success("Apartado borrado");
      }
      nav(`/admin/customers/${data.customer?.id || ""}` || "/admin/layaways");
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (!data) return <p className="text-[#8A7D71]">Cargando...</p>;

  const { layaway, customer, payments } = data;
  const remaining = layaway.total_amount - layaway.paid_amount;
  const progress = Math.min(
    100,
    (layaway.paid_amount / layaway.total_amount) * 100
  );

  return (
    <div data-testid="layaway-detail-view" className="space-y-8">
      <Link
        to="/admin/layaways"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#8A7D71] hover:text-[#C89B3C]"
      >
        <ArrowLeft size={14} /> Apartados
      </Link>

      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.4em] text-[#A66E38] mb-3">
            Apartado · {layaway.status}
          </p>
          <h1
            data-testid="layaway-invoice"
            className="font-serif text-4xl md:text-5xl font-black text-[#F5F0E6]"
          >
            {layaway.invoice_number}
          </h1>
          {customer && (
            <p className="mt-2 text-[#B8A99A]">
              Cliente:{" "}
              <Link
                to={`/admin/customers/${customer.id}`}
                className="text-[#C89B3C] hover:underline"
              >
                {customer.name}
              </Link>
            </p>
          )}
        </div>
        {layaway.status === "activo" ? (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={forgive}
              data-testid="forgive-layaway-btn"
              className="flex items-center gap-2 border border-[#C89B3C]/40 text-[#C89B3C] uppercase tracking-widest text-xs py-3 px-4 hover:bg-[#C89B3C]/10 transition-colors"
            >
              <HandCoins size={14} /> Perdonar saldo
            </button>
            <button
              onClick={cancel}
              data-testid="cancel-layaway-btn"
              className="flex items-center gap-2 border border-amber-500/40 text-amber-300 uppercase tracking-widest text-xs py-3 px-4 hover:bg-amber-500/10 transition-colors"
            >
              <XOctagon size={14} /> Cancelar
            </button>
            <button
              onClick={hardDelete}
              data-testid="delete-layaway-btn"
              className="flex items-center gap-2 border border-red-500/40 text-red-300 uppercase tracking-widest text-xs py-3 px-4 hover:bg-red-500/10 transition-colors"
            >
              <Trash2 size={14} /> Borrar cuenta
            </button>
          </div>
        ) : (
          <button
            onClick={hardDelete}
            data-testid="delete-layaway-btn"
            className="flex items-center gap-2 border border-red-500/40 text-red-300 uppercase tracking-widest text-xs py-3 px-4 hover:bg-red-500/10 transition-colors"
          >
            <Trash2 size={14} /> Borrar cuenta
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[#12100E] border border-white/5 p-5">
          <p className="text-[0.65rem] uppercase tracking-[0.35em] text-[#8A7D71] mb-2">
            Total
          </p>
          <p className="font-serif text-3xl text-[#F5F0E6]">
            {money(layaway.total_amount)}
          </p>
        </div>
        <div className="bg-[#12100E] border border-white/5 p-5">
          <p className="text-[0.65rem] uppercase tracking-[0.35em] text-[#8A7D71] mb-2">
            Pagado
          </p>
          <p
            data-testid="layaway-paid"
            className="font-serif text-3xl text-emerald-300"
          >
            {money(layaway.paid_amount)}
          </p>
        </div>
        <div className="bg-[#12100E] border border-white/5 p-5">
          <p className="text-[0.65rem] uppercase tracking-[0.35em] text-[#8A7D71] mb-2">
            Restante
          </p>
          <p
            data-testid="layaway-remaining"
            className="font-serif text-3xl text-amber-300"
          >
            {money(remaining)}
          </p>
        </div>
      </div>

      <div className="bg-[#12100E] border border-white/5 p-5">
        <div className="flex justify-between text-xs uppercase tracking-widest text-[#8A7D71] mb-2">
          <span>Progreso</span>
          <span>{progress.toFixed(0)}%</span>
        </div>
        <div className="h-2 bg-white/5 overflow-hidden">
          <div
            className="h-full bg-[#C89B3C] transition-all duration-700"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-[0.65rem] uppercase tracking-widest text-[#8A7D71]">
              Frecuencia
            </p>
            <p className="text-[#F5F0E6]">{layaway.schedule}</p>
          </div>
          <div>
            <p className="text-[0.65rem] uppercase tracking-widest text-[#8A7D71]">
              Plazo
            </p>
            <p className="text-[#F5F0E6]">{layaway.term_days} días</p>
          </div>
          <div>
            <p className="text-[0.65rem] uppercase tracking-widest text-[#8A7D71]">
              Vence
            </p>
            <p className="text-[#F5F0E6]">{dateShort(layaway.due_date)}</p>
          </div>
          <div>
            <p className="text-[0.65rem] uppercase tracking-widest text-[#8A7D71]">
              Alta
            </p>
            <p className="text-[#F5F0E6]">{dateShort(layaway.created_at)}</p>
          </div>
        </div>
      </div>

      {layaway.items?.length > 0 && (
        <div className="bg-[#12100E] border border-white/5 p-5">
          <p className="text-xs uppercase tracking-widest text-[#8A7D71] mb-3">
            Productos
          </p>
          <ul className="divide-y divide-white/5">
            {layaway.items.map((it, i) => (
              <li key={i} className="py-2 flex justify-between text-sm">
                <span className="text-[#F5F0E6]">
                  {it.qty}× {it.description}
                </span>
                <span className="text-[#B8A99A]">
                  {money(it.qty * it.unit_price)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {layaway.status === "activo" && remaining > 0 && (
        <form
          onSubmit={submit}
          data-testid="payment-form"
          className="bg-[#12100E] border border-[#A66E38]/30 p-6 md:p-8"
        >
          <h2 className="font-serif text-2xl text-[#F5F0E6] mb-5">
            Registrar pago
          </h2>

          <div className="flex gap-2 mb-6">
            {[
              { k: "efectivo", label: "Efectivo", icon: Banknote },
              { k: "valor_card", label: "Tarjeta (Valor)", icon: CreditCard },
              {
                k: "credito",
                label: `Crédito${
                  customer?.credit_balance ? ` (${money(customer.credit_balance)})` : ""
                }`,
                icon: Wallet,
                disabled: !customer?.credit_balance || customer.credit_balance <= 0,
              },
              { k: "otro", label: "Otro", icon: Banknote },
            ].map(({ k, label, icon: Icon, disabled }) => (
              <button
                key={k}
                type="button"
                disabled={disabled}
                onClick={() => setForm({ ...form, method: k })}
                data-testid={`method-${k}`}
                className={`flex items-center gap-2 text-xs uppercase tracking-widest py-2 px-4 border transition-colors ${
                  disabled
                    ? "border-white/5 text-[#5a4f45] cursor-not-allowed"
                    : form.method === k
                    ? "border-[#C89B3C] text-[#0A0908] bg-[#C89B3C]"
                    : "border-white/10 text-[#B8A99A] hover:border-[#C89B3C]/50"
                }`}
              >
                <Icon size={14} /> {label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-widest text-[#B8A99A] mb-2">
                Monto (máx {money(remaining)})
              </label>
              <input
                type="number"
                required
                min="0.01"
                step="0.01"
                max={remaining}
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                data-testid="payment-amount"
                className="w-full bg-[#0A0908] border border-white/10 text-[#F5F0E6] px-3 py-2 outline-none focus:border-[#C89B3C]"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-[#B8A99A] mb-2">
                Nota
              </label>
              <input
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                data-testid="payment-note"
                placeholder="Ej. Abono quincenal"
                className="w-full bg-[#0A0908] border border-white/10 text-[#F5F0E6] px-3 py-2 outline-none focus:border-[#C89B3C]"
              />
            </div>
          </div>

          {form.method === "valor_card" && (
            <div className="mt-5 border-t border-white/5 pt-5">
              <p className="text-[0.65rem] uppercase tracking-[0.35em] text-amber-300 mb-4">
                ⚠ Datos de tarjeta · se envían server-to-server a Valor
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs uppercase tracking-widest text-[#B8A99A] mb-2">
                    Número de tarjeta
                  </label>
                  <input
                    inputMode="numeric"
                    value={form.pan}
                    onChange={(e) => setForm({ ...form, pan: e.target.value })}
                    data-testid="card-pan"
                    placeholder="4111 1111 1111 1111"
                    className="w-full bg-[#0A0908] border border-white/10 text-[#F5F0E6] px-3 py-2 outline-none focus:border-[#C89B3C] font-mono tracking-wider"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[#B8A99A] mb-2">
                    Vencimiento (MMYY)
                  </label>
                  <input
                    inputMode="numeric"
                    maxLength={4}
                    value={form.expiry}
                    onChange={(e) => setForm({ ...form, expiry: e.target.value })}
                    data-testid="card-expiry"
                    placeholder="1229"
                    className="w-full bg-[#0A0908] border border-white/10 text-[#F5F0E6] px-3 py-2 outline-none focus:border-[#C89B3C] font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[#B8A99A] mb-2">
                    CVV
                  </label>
                  <input
                    inputMode="numeric"
                    maxLength={4}
                    value={form.cvv}
                    onChange={(e) => setForm({ ...form, cvv: e.target.value })}
                    data-testid="card-cvv"
                    placeholder="123"
                    className="w-full bg-[#0A0908] border border-white/10 text-[#F5F0E6] px-3 py-2 outline-none focus:border-[#C89B3C] font-mono"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs uppercase tracking-widest text-[#B8A99A] mb-2">
                    ZIP (AVS)
                  </label>
                  <input
                    value={form.zip_code}
                    onChange={(e) =>
                      setForm({ ...form, zip_code: e.target.value })
                    }
                    data-testid="card-zip"
                    placeholder="95023"
                    className="w-full bg-[#0A0908] border border-white/10 text-[#F5F0E6] px-3 py-2 outline-none focus:border-[#C89B3C] font-mono"
                  />
                </div>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={saving}
            data-testid="submit-payment"
            className="mt-6 w-full bg-[#A66E38] hover:bg-[#B88655] disabled:opacity-50 text-[#F5F0E6] uppercase tracking-widest text-xs py-3 transition-colors"
          >
            {saving
              ? "Procesando..."
              : form.method === "valor_card"
              ? "Cobrar tarjeta con Valor"
              : "Registrar pago"}
          </button>
        </form>
      )}

      <div>
        <h2 className="font-serif text-2xl text-[#F5F0E6] mb-4">
          Historial de pagos
        </h2>
        {payments.length === 0 ? (
          <p className="text-sm text-[#8A7D71]">Sin pagos aún.</p>
        ) : (
          <ul className="space-y-2" data-testid="payments-list">
            {payments.map((p) => (
              <li
                key={p.id}
                data-testid={`payment-row-${p.id}`}
                className="flex items-center justify-between bg-[#12100E] border border-white/5 p-4"
              >
                <div>
                  <p className="text-[#F5F0E6]">{money(p.amount)}</p>
                  <p className="text-xs text-[#8A7D71]">
                    {dateShort(p.created_at)} · {p.method}
                    {p.note ? ` · ${p.note}` : ""}
                  </p>
                  {p.valor_txn_id && (
                    <p className="text-[0.65rem] text-[#8A7D71] mt-0.5">
                      Valor txn: {p.valor_txn_id}
                    </p>
                  )}
                </div>
                <span
                  className={`text-xs uppercase tracking-widest px-2 py-1 ${
                    p.status === "aprobado"
                      ? "bg-emerald-500/15 text-emerald-300"
                      : p.status === "rechazado"
                      ? "bg-red-500/15 text-red-300"
                      : "bg-amber-500/15 text-amber-300"
                  }`}
                >
                  {p.status}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
